import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useStripe } from '@stripe/react-stripe-js';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AuthService from '../../services/AuthService';
import { canRetryPayment, getPaymentFailureText, getRetryButtonLabel } from '../../utils/paymentFailure';
import { UserPaymentMethodData } from './PaymentMethodForm';

interface RetryPaymentResponse {
    confirmed: boolean;
    clientSecret: string | null;
    paymentMethod: UserPaymentMethodData;
}

type PaymentFailureNoticeProps = {
    userId: string;
    reason?: string | null;
    // Con el acceso restringido no hay sesión: se usa la ruta sin sesión
    withSession: boolean;
    onSuccess: (paymentMethod: UserPaymentMethodData) => void;
    // Abre el cambio de tarjeta. Si no se pasa (la pantalla ya tiene su propio formulario) no se muestra esa acción
    onUpdateCard?: () => void;
    // Un reintento rechazado puede traer un motivo distinto: se avisa para que la pantalla lo muestre al instante
    onReasonChange?: (reason: string) => void;
};

// Fallo de pago de la suscripción: el motivo, la acción principal y, si la tarjeta guardada puede servir (confirmar el 3D Secure
// o volver a intentar el cobro), el botón para hacerlo. Debe estar dentro de <Elements>.
const PaymentFailureNotice = ({ userId, reason, withSession, onSuccess, onUpdateCard, onReasonChange }: PaymentFailureNoticeProps) => {
    const stripe = useStripe();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const canRetry = canRetryPayment(reason);

    const retryPayment = async () => {
        if (!stripe) {
            setError('Ha ocurrido un error. Inténtalo más tarde.');
            return;
        }

        setLoading(true);
        setError(undefined);

        try {
            // Sin token: la API cobra la factura pendiente con la tarjeta guardada
            const response = withSession
                ? await AuthService.updateSecurePaymentMethod(userId, {})
                : await AuthService.updatePaymentMethod(userId, {});
            const data: RetryPaymentResponse = response.data;

            if (data.clientSecret) {
                // El PaymentIntent de la factura ya lleva la tarjeta guardada: solo se completa la autenticación
                const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret);
                if (stripeError) {
                    setError(stripeError.message);
                    return;
                }
                if (paymentIntent?.status !== 'succeeded') {
                    setError('No se ha podido completar el pago. Inténtalo de nuevo.');
                    return;
                }
            }

            onSuccess(data.paymentMethod);
        } catch (e) {
            const data = isAxiosError<{ message?: string; paymentFailedReason?: string }>(e) ? e.response?.data : undefined;
            if (data?.paymentFailedReason) onReasonChange?.(data.paymentFailedReason);
            setError(data?.message ?? 'Error al procesar el pago. Por favor, inténtalo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                <ErrorOutlineIcon color="error" sx={{ fontSize: 22, mt: '1px' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>{getPaymentFailureText(reason)}</Typography>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {canRetry &&
                <Button variant="contained" fullWidth onClick={retryPayment} disabled={loading || !stripe}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : getRetryButtonLabel(reason)}
                </Button>
            }
            {onUpdateCard && (canRetry
                ? <Button variant="text" fullWidth size="small" onClick={onUpdateCard} disabled={loading} sx={{ mt: 1 }}>Usar otra tarjeta</Button>
                : <Button variant="contained" fullWidth onClick={onUpdateCard}>Actualizar método de pago</Button>)
            }
        </Box>
    );
};

export default PaymentFailureNotice;
